"use client";

import { createContext, useContext, useState, type ReactNode } from "react";

/**
 * Whether this visitor has given us their address on this page.
 *
 * The whole guide used to sit on the page in full, underneath a form offering to email
 * them the guide. Tom spotted it: "they get the guide without putting their email in".
 * The form was collecting nothing because there was nothing left to collect it for.
 *
 * Context rather than one big client component, because the form lives in the sticky rail
 * and the guide runs the full width underneath it. Two distant parts of a server-rendered
 * page need to agree on one boolean, and this is the smallest thing that does that.
 *
 * Soft by design. The gate is a lead-magnet gate, not a paywall: the preview is real,
 * substantial and indexable, and the point is that the email is the route to the rest, not
 * that the rest is a secret.
 */
const MagnetUnlockContext = createContext<{
  unlocked: boolean;
  unlock: () => void;
}>({ unlocked: false, unlock: () => {} });

export function MagnetUnlockProvider({ children }: { children: ReactNode }) {
  const [unlocked, setUnlocked] = useState(false);
  return (
    <MagnetUnlockContext.Provider
      value={{ unlocked, unlock: () => setUnlocked(true) }}
    >
      {children}
    </MagnetUnlockContext.Provider>
  );
}

export function useMagnetUnlock() {
  return useContext(MagnetUnlockContext);
}
